<script setup lang="ts">
import { computed } from 'vue';
import { IonContent, IonIcon } from '@ionic/vue';
import { alertCircle } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import TopBar from '@/shared/components/TopBar.vue';
import BackButton from '@/shared/components/BackButton.vue';
import useSafety from '@/modules/safety/composables/useSafety';
import useSosGesture from '@/modules/safety/composables/useSosGesture';
import useGeolocation from '@/modules/safety/composables/useGeolocation';
import useToast from '@/shared/composables/useToast';

const { t } = useI18n();
const router = useRouter();
const { reportSos, getSafetyConfig, isSendingSos, startSosLocationTracking } = useSafety();
const { config } = getSafetyConfig();
const { getCurrentPosition } = useGeolocation();
const { presentToast } = useToast();

const triggerSos = async () => {
	if (config.value?.enabled === false) {
		await presentToast('top', t('safety.disabled'), 5000, 'danger');
		router.replace({ name: 'dashboard' });
		return;
	}

	const coords = await getCurrentPosition({ timeoutMs: 10000, maximumAgeMs: 60000 });

	if (config.value?.gps_required && !coords) {
		await presentToast('top', t('safety.location_required'), 5000, 'danger');
		return;
	}

	const incident = await reportSos({
		latitude: coords?.latitude,
		longitude: coords?.longitude,
		gps_accuracy: coords?.accuracy,
	});
	if (incident) {
		startSosLocationTracking(incident.id);
		router.replace({ name: 'safety-my-report-detail', params: { id: incident.id } });
	}
};

const { register, dotsLit } = useSosGesture(triggerSos);

const dots = computed(() => [0, 1, 2].map((i) => i < dotsLit.value));
</script>

<template>
	<ion-content>
		<TopBar class="px-[34px]">
			<template #left>
				<BackButton />
			</template>
			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ t('safety.sos') }}
				</div>
			</template>
		</TopBar>

		<div class="px-[34px] pb-10 w-full max-w-[600px] mx-auto">
			<div class="rounded backdrop-blur-xl bg-white/10 p-6 text-white flex flex-col items-center text-center">
				<div class="w-[72px] h-[72px] rounded-full bg-red-600 flex items-center justify-center mb-4">
					<ion-icon :icon="alertCircle" class="text-[40px]" />
				</div>
				<h1 class="font-sourcesanspro-semibold text-m2 uppercase leading-tight">{{ t('safety.sos') }}</h1>
				<p class="font-sourcesanspro-regular text-sm2 text-white/70 mt-2 max-w-xs">{{ t('safety.sos_instruction') }}</p>

				<button :disabled="isSendingSos" class="mt-8 w-[220px] h-[220px] max-w-[78vw] max-h-[78vw] rounded-full bg-red-600 text-white flex flex-col items-center justify-center font-sourcesanspro-semibold text-l2 uppercase leading-tight text-center shadow-[0_0_0_10px_rgba(220,38,38,0.16)] active:scale-[0.97] transition-transform disabled:opacity-70" @click="register">
					<span v-if="isSendingSos">{{ t('safety.sending') }}</span>
					<span v-else>{{ t('safety.tap_three_times') }}</span>
				</button>

				<div class="flex gap-3 mt-5">
					<span v-for="(lit, i) in dots" :key="i" class="w-4 h-4 rounded-full border-2 border-white transition-colors" :class="lit ? 'bg-white' : 'bg-transparent'" />
				</div>

				<button class="text-white/70 underline text-sm2 font-sourcesanspro-regular mt-8" @click="router.back()">
					{{ t('general.cancel') }}
				</button>
			</div>
		</div>
	</ion-content>
</template>
